// @flow

import {
  add,
  compose,
  concat,
  curry,
  drop,
  inc,
  lensPath,
  lensProp,
  over,
  prop,
  propSatisfies,
  reject,
  set,
  splitAt,
  view,
} from "ramda";

import { createSelector } from "reselect";

import { modify, values as objectValuesLens } from "partial.lenses";

import { includesIn, omitIndexes } from "../util";

import { encodeAssetKey } from "../model";
import {
  FORMA_VISUALIZACAO_SERIES_POR_ATIVOS,
  IDENTIFICADOR_SERIE_CNPJ,
  IDENTIFICADOR_SERIE_CODIGO,
  IDENTIFICADOR_SERIE_NOME,
  type CurrentUser,
  type Id,
  type ModoApp,
  type Report,
  type ReportCol,
  type ReportId,
  type ReportRow,
  type ReportGridRow,
} from "../model";

import { type Action } from "./actions";

import { getModoApp } from "./selectors/comum";
import {
  identificadoresSeries,
  modoVisualizacaoSeries,
} from "./selectors/series";

type State = {
  +currentUser: ?CurrentUser,
  +reports: {
    +selected: ?ReportId,
    +records: { [ReportId]: Report },
    +loading: { [ReportId]: boolean },
    +loaded: { [ReportId]: boolean },
  },
};

const initialState: State = {
  currentUser: null,
  reports: {
    selected: "empty",
    records: {},
    loading: {},
    loaded: {},
  },
};

const currentUserLens = lensProp("currentUser");

const exportingReportLens = type => lensPath(["reports", "exporting", type]);
const selectedReportLens = lensPath(["reports", "selected"]);
const reportLens = reportId => lensPath(["reports", "records", reportId]);
const reportMetadataLens = reportId =>
  lensPath(["reports", "records", reportId, "metadata"]);
const reportRowsLens = reportId =>
  lensPath(["reports", "records", reportId, "rows"]);
const reportLoadingLens = reportId =>
  lensPath(["reports", "loading", reportId]);
const reportLoadedLens = reportId => lensPath(["reports", "loaded", reportId]);

export const currentUser: State => ?CurrentUser = view(currentUserLens);

const emptyReport = { rows: [], cols: [] };
export const report = curry(
  (reportId, state) => view(reportLens(reportId), state) || emptyReport
);
export const reportMetadata = curry(
  (reportId, state) => view(reportMetadataLens(reportId), state) || null
);
export const isLoadingReport = curry(
  (reportId, state) => view(reportLoadingLens(reportId), state) || false
);
export const hasLoadedReport = curry(
  (reportId, state) => view(reportLoadedLens(reportId), state) || false
);
export const isExportingReport = curry(
  (type, state) => view(exportingReportLens(type), state) || false
);

export const selectedReportId = view(selectedReportLens);
export const selectedReport: State => Report = state =>
  report(selectedReportId(state), state);
export const isLoadingSelectedReport: State => Report = state =>
  isLoadingReport(selectedReportId(state), state);
export const hasLoadedSelectedReport: State => Report = state =>
  hasLoadedReport(selectedReportId(state), state);

const reportCols: State => Array<ReportCol> = createSelector(
  selectedReport,
  prop("cols")
);

const seriesGridCols: State => Array<ReportCol> = createSelector(
  reportCols,
  modoVisualizacaoSeries,
  identificadoresSeries,
  (cols: Array<ReportCol>, modoVisualizacao: number, ids: Array<Id>) => {
    switch (modoVisualizacao) {
      case FORMA_VISUALIZACAO_SERIES_POR_ATIVOS: {
        const [[codigo, cnpj, nome], dataCols] = splitAt(3, cols);
        return [
          ids.includes(IDENTIFICADOR_SERIE_CODIGO) ? codigo : null,
          ids.includes(IDENTIFICADOR_SERIE_CNPJ) ? cnpj : null,
          ids.includes(IDENTIFICADOR_SERIE_NOME) ? nome : null,
          ...dataCols,
        ].filter(Boolean);
      }

      default:
        return cols;
    }
  }
);

export const selectedReportCols = createSelector(
  getModoApp,
  reportCols,
  seriesGridCols,
  (modoApp: ModoApp, cols: Array<ReportCol>, seriesCols: Array<ReportCol>) => {
    switch (modoApp) {
      case "SERIES":
        return seriesCols;

      default:
        return cols;
    }
  }
);

const selectedReportRows = createSelector(selectedReport, prop("rows"));

const selectedReportSeriesGridRows: State => Array<ReportGridRow> = createSelector(
  modoVisualizacaoSeries,
  selectedReportRows,
  identificadoresSeries,
  (modoVisualizacao: number, rows: Array<ReportRow>, ids: Array<Id>) => {
    const gridRows = rows.map(row => ({
      ...row,
      rowId: `${row.key} - ${row.values[3] || ""}`,
    }));

    switch (modoVisualizacao) {
      case FORMA_VISUALIZACAO_SERIES_POR_ATIVOS: {
        return gridRows.map(row => {
          const [codigo, cnpj, nome, ...valueCols] = row.values;

          const idCols = [];
          if (ids.includes(IDENTIFICADOR_SERIE_CODIGO)) idCols.push(codigo);
          if (ids.includes(IDENTIFICADOR_SERIE_CNPJ)) idCols.push(cnpj);
          if (ids.includes(IDENTIFICADOR_SERIE_NOME)) idCols.push(nome);

          return { ...row, values: [...idCols, ...valueCols] };
        });
      }

      default:
        return gridRows;
    }
  }
);

export const selectedReportGridRows: State => Array<ReportGridRow> = createSelector(
  getModoApp,
  selectedReportRows,
  selectedReportSeriesGridRows,
  (modoApp: ModoApp, rows: Array<ReportRow>, seriesRows: Array<ReportRow>) => {
    switch (modoApp) {
      case "SERIES":
        return seriesRows;

      default:
        return rows.map(row => ({ ...row, rowId: row.key }));
    }
  }
);

const removeAssetsWithKeys = curry((assetKeys, assets) =>
  reject(propSatisfies(includesIn(assetKeys), "key"), assets)
);

const addMetadataAssets = curry((newMetadata, metadata) =>
  compose(
    over(lensProp("ativos"), concat(newMetadata.ativos)),
    over(lensProp("linhas"), add(newMetadata.ativos.length)),
    over(lensProp("valores"), ([header, ...assets]) => [
      header,
      ...drop(1, newMetadata.valores),
      ...assets,
    ])
  )(metadata)
);

const removeAssetsFromMetadata = curry((assets, metadata) => {
  const assetKeys = new Set(assets);

  const indexesToRemove = metadata.ativos
    .map(({ identificador: id, tipoItemSelecionavel: type }, index) =>
      assetKeys.has(encodeAssetKey({ id, type })) ? index : -1
    )
    .filter(index => index >= 0);

  if (indexesToRemove.length > 0) {
    return {
      ...metadata,
      ativos: omitIndexes(indexesToRemove, metadata.ativos),
      valores: omitIndexes(indexesToRemove.map(inc), metadata.valores),
      linhas: metadata.linhas - indexesToRemove.length,
    };
  } else {
    return metadata;
  }
});

const reducer = (state: State = initialState, action: Action) => {
  switch (action.type) {
    case "SET_CURRENT_USER":
      return set(currentUserLens, action.payload, state);

    case "ASSETS_ADDED": {
      const id = selectedReportId(state);
      return set(reportLoadingLens(id), true, state);
    }

    case "SET_REPORT_LOADING": {
      const {
        meta: { reportId },
        payload: loading,
      } = action;
      return set(reportLoadingLens(reportId), loading, state);
    }

    case "FETCH_REPORT": {
      const {
        meta: { reportId },
      } = action;
      return compose(
        set(selectedReportLens, reportId),
        set(reportLoadingLens(reportId), true)
      )(state);
    }

    case "EXPORT_REPORT": {
      const { payload: type } = action;
      return set(exportingReportLens(type), true, state);
    }

    case "REPORT_EXPORTED": {
      const { payload: type } = action;
      return set(exportingReportLens(type), false, state);
    }

    case "REPORT_EXPORTING_ERROR": {
      const { payload: type } = action;
      return set(exportingReportLens(type), false, state);
    }

    case "REMOVE_ASSETS": {
      const {
        meta: { reportId },
        payload: assetKeys,
      } = action;
      return compose(
        over(reportRowsLens(reportId), removeAssetsWithKeys(assetKeys)),
        over(reportMetadataLens(reportId), removeAssetsFromMetadata(assetKeys))
      )(state);
    }

    case "ASSETS_REMOVED": {
      const { payload: assetKeys } = action;
      return compose(
        modify(
          ["reports", "records", objectValuesLens, "rows"],
          removeAssetsWithKeys(assetKeys)
        ),
        modify(
          ["reports", "records", objectValuesLens, "metadata"],
          removeAssetsFromMetadata(assetKeys)
        )
      )(state);
    }

    case "ADD_REPORT_ROWS": {
      const {
        meta: { reportId },
        payload: { rows, metadata },
      } = action;
      return compose(
        over(reportRowsLens(reportId), concat(rows)),
        over(reportMetadataLens(reportId), addMetadataAssets(metadata))
      )(state);
    }

    case "REPORT_FETCHED": {
      const {
        meta: { reportId },
        payload: report,
      } = action;
      return compose(
        set(reportLens(reportId), report),
        set(reportLoadedLens(reportId), true),
        set(reportLoadingLens(reportId), false)
      )(state);
    }

    case "REPORT_FETCHING_ERROR": {
      const {
        meta: { reportId },
      } = action;
      return compose(
        set(reportLoadedLens(reportId), false),
        set(reportLoadingLens(reportId), false)
      )(state);
    }

    default:
      return state;
  }
};

export default reducer;
